// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrderApprovals from "@insite/client-framework/Store/Data/OrderApprovals/Handlers/LoadOrderApprovals";
import { getOrderApprovalsDataView } from "@insite/client-framework/Store/Data/OrderApprovals/OrderApprovalsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderApprovalList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { PageProps } from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pace/App/Components/Common/SectionWrapper";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import { DataTableHeaderProps } from "@pace/MobiusOverrides/DataTable/DataTableHeader";
import { sharedDateTimeOptions, USER_ROLES } from "@pace/XDL/constants";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    sectionTitle = "sectionTitle",
    linkText = "linkText",
    numberOfOrderApprovals = "numberOfOrderApprovals",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.linkText]: string;
        [fields.numberOfOrderApprovals]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        adminRole: xHasRoles(state, [USER_ROLES.ADMIN]),
        buyer3Role: xHasRoles(state, [USER_ROLES.BUYER3]),
        getOrderApprovalsParameter: {
            ...state.pages.orderApprovalList.getOrderApprovalsParameter,
            pageSize: ownProps.fields.numberOfOrderApprovals,
        },
        orderApprovalsDataView: getOrderApprovalsDataView(state, {
            ...state.pages.orderApprovalList.getOrderApprovalsParameter,
            pageSize: ownProps.fields.numberOfOrderApprovals,
        }),

        language: state.context.session.language,
        orderApprovalsLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
        orderApprovalDetailsLink: getPageLinkByPageType(state, "OrderApprovalDetailsPage"),
        isAuthenticated: getSession(state).isAuthenticated && !getSession(state).isGuest,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadOrderApprovals,
};

type Props = PageProps & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderApprovalsStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderNumberHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    orderNumberCell?: DataTableCellBaseProps;
    orderDateCell?: DataTableCellBaseProps;
    totalCell?: DataTableCellBaseProps;
    sectionWrapperStyles?: SectionWrapperStyles;
}

export const orderApprovalsStyles: OrderApprovalsStyles = {};

const styles = orderApprovalsStyles;

const OrderApprovalsWidget = ({
    fields,
    adminRole,
    buyer3Role,
    getOrderApprovalsParameter,
    language,
    orderApprovalsLink,
    orderApprovalDetailsLink,
    orderApprovalsDataView,
    isAuthenticated,
    loadOrderApprovals,
}: Props) => {
    if (!isAuthenticated || !(adminRole || buyer3Role)) {
        return null;
    }

    React.useEffect(() => {
        if (!orderApprovalsDataView.value && !orderApprovalsDataView.isLoading) {
            loadOrderApprovals(getOrderApprovalsParameter);
        }
    });

    if (!orderApprovalsDataView.value || orderApprovalsDataView.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    return (
        <SectionWrapper
            title={fields.sectionTitle}
            titleVariant="small"
            extendedStyles={styles.sectionWrapperStyles}
            link={orderApprovalsLink?.url}
            linkText={fields.linkText}
            data-test-selector="orderApprovals_tableWidget"
        >
            {orderApprovalsDataView.value.length === 0 ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText}>{translate("No order approvals found.")}</Typography>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader tight {...styles.orderNumberHeader}>
                                {translate("Order #")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.orderDateHeader}>
                                {translate("Order Date")}
                            </DataTableHeader>

                            <DataTableHeader tight {...styles.totalHeader}>
                                {translate("Order Total")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {orderApprovalsDataView.value.map(order => (
                                <DataTableRow
                                    data-test-selector="orderApproval_orderLine"
                                    data-test-key={order.id}
                                    key={order.id}
                                    {...styles.dataTableRow}
                                >
                                    <DataTableCell
                                        {...styles.orderNumberCell}
                                        data-test-selector="orderApproval_orderNumber"
                                    >
                                        <Link href={`${orderApprovalDetailsLink?.url}?cartId=${order.id}`}>
                                            {order.orderNumber}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.orderDateCell}>
                                        {order.orderDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(order.orderDate),
                                                  language,
                                                  options: {
                                                    // XNG-Changes
                                                      ...sharedDateTimeOptions,
                                                  },
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell {...styles.totalCell}>{order.orderGrandTotalDisplay}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            )}
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderApprovalsWidget),
    definition: {
        group: "Common",
        displayName: "Order Approvals",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                displayName: "Section Title",
                editorTemplate: "TextField",
                defaultValue: "Orders Awaiting Approval",
                fieldType: "Translatable",
            },
            {
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "View All Order Approvals",
                fieldType: "Translatable",
            },
            {
                name: fields.numberOfOrderApprovals,
                displayName: "Number of Order Approvals Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
